import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { Helmet } from '@/pages/home/components/SEO';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于韩漫网 - 韩漫大全 | 下拉式韩国漫画在线观看平台',
  description: '韩漫网整合韩漫大全、下拉式韩国漫画和在线观看功能，提供一站式韩漫阅读解决方案。了解我们的品牌故事、用户口碑和联系方式。',
  url: 'https://hanmanwang.com/about',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩漫网的漫画都是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫网提供大量免费韩国漫画资源，您可以在线观看和下拉式阅读，无需付费即可享受优质的韩漫阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '什么是下拉式韩国漫画阅读？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '下拉式阅读是韩漫网专为韩国漫画优化的阅读模式。您只需向下滚动页面，漫画内容就会自动加载，无需手动翻页，带来连贯流畅的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫网多久更新一次漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫网每日更新，与韩国最新连载保持同步。我们的编辑团队全天候关注韩漫动态，确保您在第一时间阅读到最新的章节。',
      },
    },
    {
      '@type': 'Question',
      name: '可以在手机上使用韩漫网吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '当然可以！韩漫网完美适配电脑、平板和手机等多种设备。无论您使用什么设备访问，都能获得最佳的下拉式韩国漫画阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在韩漫网找到我想看的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过韩漫网的分类导航浏览不同类别的漫画，包括恋爱、奇幻、动作、搞笑、悬疑、剧情、日常、校园、恐怖等多种类型。也可以在首页查看精选推荐和人气排行。',
      },
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <Helmet
        title="关于我们 - 韩漫网 | 韩漫大全 下拉式韩国漫画在线观看平台"
        description="了解韩漫网——整合韩漫大全、下拉式韩国漫画和在线观看功能的一站式韩漫平台。查看品牌介绍、用户口碑和联系方式，开启您的韩漫阅读之旅。"
        keywords="韩漫网,韩漫大全,关于韩漫网,下拉式韩国漫画,韩漫在线观看,韩漫联系方式"
        canonicalUrl="https://hanmanwang.com/about"
        schema={[aboutSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]} />
            </div>

            <section className="py-8 md:py-12 text-center">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于<strong>韩漫网</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                韩漫网 —— 您的一站式韩国漫画阅读平台，整合<strong>韩漫大全</strong>、<strong>下拉式韩国漫画</strong>和<strong>在线观看</strong>功能
              </p>
            </section>

            <section className="py-10 md:py-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl text-foreground-900 mb-5">品牌故事</h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      韩漫网成立于2024年，由一群热爱韩国漫画的资深漫迷共同创建。我们发现国内韩漫爱好者面临资源分散、阅读体验差、频繁跳转等问题，于是下定决心打造一个专业的一站式韩漫阅读平台。
                    </p>
                    <p>
                      经过团队的不懈努力，韩漫网如今已发展成为国内领先的韩漫在线阅读平台，收录超过<strong>10,000部</strong>优质韩国漫画，涵盖恋爱、奇幻、动作、搞笑、悬疑、剧情、日常、校园、恐怖等多种类型。
                    </p>
                    <p>
                      我们独创的<strong>下拉式阅读模式</strong>，彻底改变了传统的翻页阅读方式，让读者可以像浏览网页一样自然流畅地阅读漫画，无需跳转、无需等待，一气呵成。
                    </p>
                    <p>
                      韩漫网始终秉持"内容为王、用户体验至上"的理念，每日更新最新的韩国漫画连载，为超过<strong>500万</strong>活跃读者提供优质的韩漫阅读服务。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20modern%20office%20space%20with%20colorful%20comic%20book%20posters%20on%20walls%2C%20bookshelves%20filled%20with%20comics%2C%20cozy%20reading%20nook%20with%20warm%20lighting%2C%20creative%20workspace%20vibe%2C%20Korean%20webtoon%20style%20decorations%2C%20artistic%20atmosphere&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="韩漫网品牌故事"
                    title="韩漫网 - 品牌故事"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="font-heading text-2xl text-foreground-900 mb-2 text-center">用户口碑</h2>
                <p className="text-sm text-foreground-500 text-center mb-8">来自真实读者的评价</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      name: '小雨',
                      role: '大学生',
                      content: '韩漫网真的是我每天必刷的网站！下拉式阅读太舒服了，不用一直点翻页，一口气看完十几话完全不过瘾。资源也很全，想看的韩漫基本都能找到。',
                      rating: 5,
                    },
                    {
                      name: '阿强',
                      role: '上班族',
                      content: '通勤路上用手机看韩漫网已经成为习惯，适配做得特别好，手机上看也很清晰。更新速度也很快，追的几部热门韩漫都是同步更新。',
                      rating: 5,
                    },
                    {
                      name: '小美',
                      role: '自由职业者',
                      content: '之前看韩漫要跳转好几个网站，特别麻烦。韩漫网一站式搞定，从分类浏览到在线观看都很流畅。推荐给了好多朋友，大家都说好用！',
                      rating: 4,
                    },
                    {
                      name: '大鹏',
                      role: '设计师',
                      content: '作为视觉设计师，我很在意网页的阅读体验。韩漫网的界面简洁美观，没有任何干扰元素，纯粹为阅读而生。下拉式阅读的设计真的很用心。',
                      rating: 5,
                    },
                    {
                      name: '乐乐',
                      role: '高中生',
                      content: '平时学习压力大，看韩漫是我最好的放松方式。韩漫网分类很清晰，搞笑类漫画是我的最爱，每次看都能笑出声，强烈推荐给大家！',
                      rating: 5,
                    },
                    {
                      name: '老王',
                      role: '程序员',
                      content: '技术角度来说，韩漫网的加载速度很快，下拉加载也很丝滑。资源覆盖面广，从热门到小众的韩漫都有，能满足不同读者的需求。',
                      rating: 4,
                    },
                  ].map((review, i) => (
                    <div key={i} className="p-5 rounded-lg bg-background-50">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center font-heading text-accent-700 text-sm">
                          {review.name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                        &ldquo;{review.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, j) => (
                          <span key={j} className={`w-3.5 h-3.5 flex items-center justify-center ${j < review.rating ? 'text-accent-500' : 'text-background-300'}`}>
                            <i className={`${j < review.rating ? 'ri-star-fill' : 'ri-star-line'} text-xs`}></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="font-heading text-2xl text-foreground-900 mb-2 text-center">常见问题</h2>
                <p className="text-sm text-foreground-500 text-center mb-8">关于韩漫网的常见疑问解答</p>

                <div className="max-w-3xl mx-auto space-y-3">
                  {[
                    { q: '韩漫网的漫画都是免费的吗？', a: '韩漫网提供大量免费韩国漫画资源，您可以在线观看和下拉式阅读，无需付费即可享受优质的韩漫阅读体验。' },
                    { q: '什么是下拉式韩国漫画阅读？', a: '下拉式阅读是韩漫网专为韩国漫画优化的阅读模式。您只需向下滚动页面，漫画内容就会自动加载，无需手动翻页，带来连贯流畅的阅读体验。' },
                    { q: '韩漫网多久更新一次漫画？', a: '韩漫网每日更新，与韩国最新连载保持同步。我们的编辑团队全天候关注韩漫动态，确保您在第一时间阅读到最新的章节。' },
                    { q: '可以在手机上使用韩漫网吗？', a: '当然可以！韩漫网完美适配电脑、平板和手机等多种设备。无论您使用什么设备访问，都能获得最佳的下拉式韩国漫画阅读体验。' },
                    { q: '如何在韩漫网找到我想看的漫画？', a: '您可以通过韩漫网的分类导航浏览不同类别的漫画，包括恋爱、奇幻、动作、搞笑、悬疑、剧情、日常、校园、恐怖等多种类型。也可以在首页查看精选推荐和人气排行。' },
                  ].map((faq, i) => (
                    <details key={i} className="group border border-background-200 rounded-lg overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                        <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform shrink-0">
                          <i className="ri-arrow-down-s-line text-sm"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <h2 className="font-heading text-2xl text-foreground-900 mb-5">联系方式</h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                            <i className="ri-mail-line text-lg"></i>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                          <p className="text-sm text-foreground-600">contact@hanmanwang.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                            <i className="ri-phone-line text-lg"></i>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">客服电话</p>
                          <p className="text-sm text-foreground-600">400-888-6666</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                            <i className="ri-map-pin-line text-lg"></i>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">公司地址</p>
                          <p className="text-sm text-foreground-600">上海市浦东新区张江高科技园区</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
                            <i className="ri-time-line text-lg"></i>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">工作时间</p>
                          <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="font-heading text-2xl text-foreground-900 mb-5">给我们留言</h2>
                    <form
                      className="space-y-4"
                      data-readdy-form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                        if (honeypot) {
                          alert('感谢您的留言，我们会尽快回复！');
                          return;
                        }
                        const formData = new FormData(form);
                        formData.delete('phone_alt');
                        try {
                          const res = await fetch('https://readdy.ai/api/form/d9pc5siftmtnfcfh0kfg', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                          });
                          if (res.ok) {
                            alert('感谢您的留言，我们会尽快回复！');
                            form.reset();
                          } else {
                            const text = await res.text();
                            alert(text || '提交失败，请稍后重试');
                          }
                        } catch {
                          alert('网络错误，请稍后重试');
                        }
                      }}
                    >
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言内容..."
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        />
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="hp-field"
                      />
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}